import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { DynamicIcon } from '../components/icons/DynamicIcon';
import toast from 'react-hot-toast';

export default function Profile() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState({
    nome: '',
    sobrenome: '',
    nome_empresa: '',
    telefone: '',
    cidade: '',
    estado: '',
    instagram: '',
    cnpj: '',
    tipo_servico: 'freelancer',
    anos_experiencia: 0,
    especialidades: [],
    faixa_preco: 'medio',
    bio: '',
    website: '',
    endereco_completo: '',
    aceita_cartao: true,
    aceita_pix: true
  });

  const estadosBrasil = [
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
    'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
    'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
  ];

  const tiposServico = [
    { value: 'freelancer', label: 'Bartender Freelancer' },
    { value: 'empresa', label: 'Empresa de Eventos' },
    { value: 'equipe', label: 'Equipe de Bartenders' }
  ];

  const faixasPreco = [
    { value: 'economico', label: 'Econômico' },
    { value: 'medio', label: 'Médio' },
    { value: 'premium', label: 'Premium' }
  ];

  const especialidadesOpcoes = [
    'Casamentos',
    'Aniversários',
    'Eventos Corporativos',
    'Formaturas',
    'Festivais',
    'Happy Hour',
    'Confraternizações'
  ];

  useEffect(() => {
    fetchProfile();
  }, [user]);

  const fetchProfile = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (data) {
        setProfile(data);
      }
    } catch (err) {
      console.log('Perfil ainda não criado');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const profileData = {
        ...profile,
        user_id: user.id
      };

      const { data: existingProfile } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (existingProfile) {
        // Update
        const { error } = await supabase
          .from('user_profiles')
          .update(profileData)
          .eq('user_id', user.id);

        if (error) throw error;
      } else {
        // Insert
        const { error } = await supabase
          .from('user_profiles')
          .insert([profileData]);

        if (error) throw error;
      }

      toast.success('Perfil salvo com sucesso!');
    } catch (error) {
      console.error('Erro:', error);
      toast.error('Erro ao salvar perfil');
    } finally {
      setLoading(false);
    }
  };

  const toggleEspecialidade = (esp) => {
    setProfile(prev => ({
      ...prev,
      especialidades: prev.especialidades.includes(esp)
        ? prev.especialidades.filter(e => e !== esp)
        : [...prev.especialidades, esp]
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <DynamicIcon name="user" size={32} className="text-blue-600 mr-3" />
              <h1 className="text-2xl font-bold text-gray-900">Meu Perfil Profissional</h1>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <DynamicIcon name="arrowLeft" size={16} />
              Voltar ao Dashboard
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Informações Pessoais */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <DynamicIcon name="user" size={20} className="text-purple-600" />
              Informações Pessoais
            </h2>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Nome</label>
                <input
                  type="text"
                  required
                  className="w-full border rounded-lg px-3 py-2"
                  value={profile.nome}
                  onChange={(e) => setProfile({...profile, nome: e.target.value})}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Sobrenome</label>
                <input
                  type="text"
                  required
                  className="w-full border rounded-lg px-3 py-2"
                  value={profile.sobrenome}
                  onChange={(e) => setProfile({...profile, sobrenome: e.target.value})}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Telefone</label>
                <input
                  type="tel"
                  placeholder="(11) 98765-4321"
                  className="w-full border rounded-lg px-3 py-2"
                  value={profile.telefone}
                  onChange={(e) => setProfile({...profile, telefone: e.target.value})}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Instagram</label>
                <div className="flex">
                  <span className="inline-flex items-center px-3 rounded-l-lg border border-r-0 bg-gray-50">
                    @
                  </span>
                  <input
                    type="text"
                    placeholder="seuperfil"
                    className="flex-1 border rounded-r-lg px-3 py-2"
                    value={profile.instagram}
                    onChange={(e) => setProfile({...profile, instagram: e.target.value})}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Informações Profissionais */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <DynamicIcon name="briefcase" size={20} className="text-purple-600" />
              Informações Profissionais
            </h2>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">Nome da Empresa</label>
                <input
                  type="text"
                  className="w-full border rounded-lg px-3 py-2"
                  value={profile.nome_empresa}
                  onChange={(e) => setProfile({...profile, nome_empresa: e.target.value})}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">CNPJ</label>
                <input
                  type="text"
                  placeholder="00.000.000/0000-00"
                  className="w-full border rounded-lg px-3 py-2"
                  value={profile.cnpj}
                  onChange={(e) => setProfile({...profile, cnpj: e.target.value})}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Website</label>
                <input
                  type="url"
                  placeholder="https://seusite.com.br"
                  className="w-full border rounded-lg px-3 py-2"
                  value={profile.website}
                  onChange={(e) => setProfile({...profile, website: e.target.value})}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Tipo de Serviço</label>
                <select
                  className="w-full border rounded-lg px-3 py-2"
                  value={profile.tipo_servico}
                  onChange={(e) => setProfile({...profile, tipo_servico: e.target.value})}
                >
                  {tiposServico.map(tipo => (
                    <option key={tipo.value} value={tipo.value}>
                      {tipo.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Anos de Experiência</label>
                <input
                  type="number"
                  min="0"
                  className="w-full border rounded-lg px-3 py-2"
                  value={profile.anos_experiencia}
                  onChange={(e) => setProfile({...profile, anos_experiencia: parseInt(e.target.value) || 0})}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Faixa de Preço</label>
                <select
                  className="w-full border rounded-lg px-3 py-2"
                  value={profile.faixa_preco}
                  onChange={(e) => setProfile({...profile, faixa_preco: e.target.value})}
                >
                  {faixasPreco.map(faixa => (
                    <option key={faixa.value} value={faixa.value}>
                      {faixa.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">Bio / Descrição</label>
                <textarea
                  rows={4}
                  placeholder="Conte um pouco sobre você e seus serviços..."
                  className="w-full border rounded-lg px-3 py-2"
                  value={profile.bio}
                  onChange={(e) => setProfile({...profile, bio: e.target.value})}
                />
              </div>
            </div>

            {/* Especialidades */}
            <div className="mt-4">
              <label className="block text-sm font-medium mb-2">Especialidades</label>
              <div className="flex flex-wrap gap-2">
                {especialidadesOpcoes.map(esp => (
                  <button
                    key={esp}
                    type="button"
                    onClick={() => toggleEspecialidade(esp)}
                    className={`px-3 py-1 rounded-full text-sm transition-colors ${
                      profile.especialidades.includes(esp)
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-100 hover:bg-gray-200'
                    }`}
                  >
                    {esp}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Localização */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <DynamicIcon name="mapPin" size={20} className="text-purple-600" />
              Localização
            </h2>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Cidade</label>
                <input
                  type="text"
                  className="w-full border rounded-lg px-3 py-2"
                  value={profile.cidade}
                  onChange={(e) => setProfile({...profile, cidade: e.target.value})}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Estado</label>
                <select
                  className="w-full border rounded-lg px-3 py-2"
                  value={profile.estado}
                  onChange={(e) => setProfile({...profile, estado: e.target.value})}
                >
                  <option value="">Selecione</option>
                  {estadosBrasil.map(uf => (
                    <option key={uf} value={uf}>{uf}</option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">Endereço Completo</label>
                <input
                  type="text"
                  placeholder="Rua, número, bairro..."
                  className="w-full border rounded-lg px-3 py-2"
                  value={profile.endereco_completo}
                  onChange={(e) => setProfile({...profile, endereco_completo: e.target.value})}
                />
              </div>
            </div>
          </div>

          {/* Formas de Pagamento */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <DynamicIcon name="creditCard" size={20} className="text-purple-600" />
              Formas de Pagamento Aceitas
            </h2>

            <div className="space-y-3">
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={profile.aceita_cartao}
                  onChange={(e) => setProfile({...profile, aceita_cartao: e.target.checked})}
                  className="w-4 h-4 text-purple-600"
                />
                <span className="flex items-center gap-2">
                  <DynamicIcon name="creditCard" size={16} />
                  Cartão de Crédito/Débito
                </span>
              </label>

              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={profile.aceita_pix}
                  onChange={(e) => setProfile({...profile, aceita_pix: e.target.checked})}
                  className="w-4 h-4 text-purple-600"
                />
                <span className="flex items-center gap-2">
                  <DynamicIcon name="smartphone" size={16} />
                  PIX
                </span>
              </label>
            </div>
          </div>

          {/* Botão Salvar */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-lg flex items-center justify-center gap-2 transition-colors"
          >
            <DynamicIcon name="save" size={20} />
            {loading ? 'Salvando...' : 'Salvar Perfil'}
          </button>
        </form>
      </div>
    </div>
  );
}